import React, { useState, useCallback, memo, useEffect, useRef, KeyboardEvent } from 'react';
import { Button, InputGroup, FormControl, Alert, Table } from 'react-bootstrap';
import { Branch, Lender } from './Import';

type SearchBarProps = {
    onClose: () => void;
    searchValues: Branch[] | Lender[];
    onSelect: (branch: Branch | Lender) => void;
    setSelectedID?: React.Dispatch<React.SetStateAction<number>>
    searchType: string;
    onAddNewLender?: (searchTerm: string) => void;
};

const SearchBar: React.FC<SearchBarProps> = memo(({ onClose, searchValues, onSelect, setSelectedID, searchType, onAddNewLender }) => {
    const [suggestions, setSuggestions] = useState<Branch[] | Lender[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchTerm(value);

        if (searchValues && value.length > 3) {
            let filteredSuggestions;
            if (searchType === 'lender') {
                filteredSuggestions= (searchValues as Lender[]).filter((lender) => {
                    const searchString = lender['Name'];
                    return searchString && searchString.toLowerCase().includes(value.toLowerCase());
                }).slice(0, 100);
                setSuggestions(filteredSuggestions as Lender[]);
                setSelectedIndex(-1);
            } else {
                filteredSuggestions = (searchValues as Branch[]).filter((branch) => {
                    const searchString = branch['Branch'] ? `${branch['Branch']}: ${branch['Address']}` : branch['Address'];
                    return searchString && searchString.toLowerCase().includes(value.toLowerCase());
                }).slice(0, 100);
                    setSuggestions(filteredSuggestions as Branch[]);
                    setSelectedIndex(-1);
            }
        } else {
            setSuggestions([]);
        }
    }, [searchValues]);

    const handleSelect = useCallback((selectedTerm: Branch | Lender) => {
        let displayName : string;
        if (searchType === 'lender') {
            setSearchTerm(selectedTerm['Name']);
            onSelect(selectedTerm as Lender);
            // setSelectedID(selectedTerm['LDDLenderID'] as number);
        }
        else{
            displayName = selectedTerm['Branch'] ? `${selectedTerm['Branch']}: ${selectedTerm['Address']}` : selectedTerm['Address'];
            setSearchTerm(displayName as string);
            onSelect(selectedTerm as Branch);
            setSelectedID?.(selectedTerm['LenderContactID'] as number);
        }
        setSuggestions([]);
    }, [onSelect]);

    const getHighlightedText = useCallback((text: string, highlight: string) => {
        const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
        return (
            <span>
                {searchType === 'lender' ? <i className="fa-solid fa-building-columns"></i> : <i className="fas fa-location-dot me-2"></i>}
                {parts.map((part, index) =>
                    part.toLowerCase() === highlight.toLowerCase() ? <b key={index}>{part}</b> : part
                )}
            </span>
        );
    }, [searchType]);

    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
        if (suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prevIndex => 
                    prevIndex < suggestions.length - 1 ? prevIndex + 1 : prevIndex
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : -1));
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0) {
                    handleSelect(suggestions[selectedIndex]);
                }
                break;
            case 'Escape':
                onClose();
                break;
        }
    }, [suggestions, selectedIndex, handleSelect, onClose]);

    useEffect(() => {
        if (searchTerm) {
            handleSearch({ target: { value: searchTerm } } as React.ChangeEvent<HTMLInputElement>);
        }
    }, [searchValues, handleSearch, searchTerm]);

    useEffect(() => {
        if (selectedIndex >= 0 && suggestionsRef.current) {
            const selectedElement = suggestionsRef.current.children[selectedIndex] as HTMLElement;
            selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selectedIndex]);

    return (
        <>
            <div className="mb-2">
                <InputGroup className="search-bar">
                    <InputGroup.Text>
                        <i className="fa-solid fa-magnifying-glass status-secondary"/>
                    </InputGroup.Text>
                    <FormControl
                        ref={inputRef}
                        placeholder={searchType === 'lender' ? "Enter a lender..." : "Enter a location..."}
                        aria-label={searchType === 'lender' ? "Search lender" : "Search location"}
                        value={searchTerm}
                        onChange={handleSearch}
                        onKeyDown={handleKeyDown}
                    />
                    <Button onClick={onClose} aria-label="Close" variant="link">
                        <i className="fa-regular fa-xmark"></i>
                    </Button>
                </InputGroup>
            </div>

            {
                searchTerm.length > 3 && suggestions.length === 0 && searchType !== 'lender' &&
                <Alert variant="warning">No locations available.</Alert>
            }
            {
                searchTerm.length > 3 && suggestions.length === 0 && searchType === 'lender' &&
                <Alert variant="warning" className="d-flex align-items-center">
                    <span>
                        Lender not found. Would you like to{' '}
                        <Button variant="link" className="p-0 align-baseline text-primary text-decoration-underline" onClick={() => onAddNewLender?.(searchTerm)}>
                            add a new lender
                        </Button>
                        ?
                    </span>
                </Alert>
            }

            {suggestions.length > 0 && (
                <div ref={suggestionsRef} style={{ maxHeight: '240px', overflowY: 'auto' }}>
                    <Table hover>
                        <tbody>
                            {suggestions.map((suggestion) => (
                                <tr
                                    key={searchType === 'lender' ? suggestion.LDDLenderID : suggestion.LenderContactID}
                                    onClick={() => handleSelect(suggestion)}
                                >
                                    <td>
                                        {getHighlightedText(
                                            searchType === 'lender' ? suggestion.Name 
                                            : suggestion.Branch ? `${suggestion.Branch}: ${suggestion.Address}` 
                                                                : suggestion.Address || 'Unknown Branch', searchTerm
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}
        </>
    );
});

export default SearchBar;